import { cx } from "components/Graphs/utils";
import React from "react";

import ShellUtilityAction from "../ShellUtilityAction";
import type { ApplicationEnvironmentTone } from "../environmentPresentation";
import type { ShellUtility } from "../types";

export interface ProductTopBarProps {
  brand: React.ReactNode;
  workspaceContext?: React.ReactNode;
  actions?: React.ReactNode;
  balance?: React.ReactNode;
  utilities: ShellUtility[];
  userMenu?: React.ReactNode;
  onUtilityAction: (utilityId: string) => void;
  ariaLabel?: string;
  className?: string;
  environmentTone?: ApplicationEnvironmentTone;
}

export const ProductTopBar: React.FC<ProductTopBarProps> = ({
  brand,
  workspaceContext,
  actions,
  balance,
  utilities,
  userMenu,
  onUtilityAction,
  ariaLabel = "Application header",
  className,
  environmentTone,
}) => (
  <header
    className={cx(
      "relative flex h-full min-w-0 items-center border-b border-zinc-800 bg-[#090c0a] px-4 text-primary shadow-[0_1px_0_rgba(255,255,255,0.025),0_8px_28px_rgba(0,0,0,0.24)]",
      environmentTone === "staging" && "border-t-[3px] border-t-[#BD5421]",
      environmentTone === "development" &&
        "border-t-[3px] border-t-highlight dark:border-t-cyan-700",
      className,
    )}
    style={{
      backgroundImage:
        "linear-gradient(90deg, rgba(118,185,0,0.028), transparent 22%, transparent 78%, rgba(118,185,0,0.018)), repeating-linear-gradient(100deg, rgba(255,255,255,0.012) 0, rgba(255,255,255,0.012) 1px, transparent 1px, transparent 4px)",
    }}
    aria-label={ariaLabel}
    data-environment-tone={environmentTone}
  >
    <div className="flex min-w-0 flex-1 items-center overflow-hidden">
      <div className="flex shrink-0 items-center overflow-hidden">{brand}</div>
      {workspaceContext ? (
        <>
          <span
            className="mx-2 hidden shrink-0 font-mono text-base text-zinc-800 sm:block"
            aria-hidden="true"
          >
            |
          </span>
          <div className="min-w-0 overflow-hidden">{workspaceContext}</div>
        </>
      ) : null}
    </div>

    <div className="ml-3 flex shrink-0 items-center gap-1">
      {actions ? <div className="shrink-0">{actions}</div> : null}
      {actions && (balance || utilities.length > 0 || userMenu) ? (
        <span
          className="mx-1 hidden h-5 w-px bg-zinc-800 sm:block"
          aria-hidden="true"
          data-shell-topbar-divider
        />
      ) : null}
      {balance ? <div className="mr-1 shrink-0">{balance}</div> : null}
      {balance && utilities.length > 0 ? (
        <span
          className="mx-1 hidden h-5 w-px bg-zinc-800 sm:block"
          aria-hidden="true"
          data-shell-topbar-divider
        />
      ) : null}
      {utilities.map((utility) => (
        <ShellUtilityAction
          key={utility.id}
          utility={utility}
          onAction={onUtilityAction}
        />
      ))}
      {(balance || utilities.length > 0) && userMenu ? (
        <span
          className="mx-1 hidden h-5 w-px bg-zinc-800 sm:block"
          aria-hidden="true"
          data-shell-topbar-divider
        />
      ) : null}
      {userMenu ? (
        <div className="max-w-40 shrink-0 overflow-hidden">{userMenu}</div>
      ) : null}
    </div>
  </header>
);

ProductTopBar.displayName = "ProductTopBar";

export default ProductTopBar;
